import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  Clock,
  Users,
  User,
  Eye,
  Moon,
  ChevronDown,
  ShieldAlert,
  Activity,
} from 'lucide-react';
import { StatusBadge } from '../UI/StatusBadge';
import { InfoBadge } from '../UI/InfoBadge';
import { FilterButton } from '../UI/FilterButton';
import PillSelect from '../UI/PillSelect';
import DebouncedSearchInput from '../UI/DebouncedSearchInput';
import { staffAPI } from '../../services/api';
import { uniquePersonnelListCount } from '../../utils/assignedPersonnel';
import { formatHrActionBannerTitle } from '../../utils/workforceRequestLabels';
import { formatDate, formatDateRange, formatDateShort } from '../../utils/dateUtils';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_deputy_hod', label: 'Pending Dy HOD' },
  { value: 'pending_hod', label: 'Pending HOD' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'frozen', label: 'Amendment Pending' },
];

const MAX_INLINE_DATES = 3;

const buildDateLabel = (request) => {
  if (request.date_selection_type === 'range') {
    return formatDateRange(request.start_date, request.end_date);
  }
  const dates = request.nightwork_dates || [];
  if (dates.length === 0) return '—';
  if (dates.length <= MAX_INLINE_DATES) {
    return dates.map(formatDateShort).join(', ');
  }
  const shown = dates.slice(0, MAX_INLINE_DATES).map(formatDateShort).join(', ');
  return `${shown} +${dates.length - MAX_INLINE_DATES} more`;
};

const formatTime = (t) => {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const NightworkRequestList = ({ mode, onViewDetail, currentUser }) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const queryConfig = {
    'pending-approval': { key: ['nightwork-pending'], fn: staffAPI.getNightworkPendingApproval },
    'my-requests': { key: ['nightwork-my-requests'], fn: staffAPI.getNightworkMyRequests },
    'all-requests': { key: ['nightwork-all-requests'], fn: staffAPI.getNightworkAllRequests },
  };
  const { key: queryKey, fn: queryFn } = queryConfig[mode] || queryConfig['my-requests'];

  const { data: requests, isLoading, error } = useQuery({
    queryKey,
    queryFn,
    select: r => r.data,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  const filtered = useMemo(() => {
    if (!requests) return [];
    let list = [...requests];
    if (statusFilter) list = list.filter(r => r.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        r.request_number?.toLowerCase().includes(q) ||
        r.project_code?.toLowerCase().includes(q) ||
        r.project_name?.toLowerCase().includes(q) ||
        r.requested_by_name?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [requests, statusFilter, search]);

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-8">
        <div className="flex items-center justify-center gap-3 text-gray-500">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-800" />
          <span className="text-sm">Loading requests...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border p-8 text-center">
        <p className="text-sm text-red-600">Failed to load requests. Please try again.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white rounded-2xl shadow-sm border p-4">
        <div className="flex items-center gap-3">
          <DebouncedSearchInput
            value={search}
            onSearch={setSearch}
            placeholder="Search by request number, project, or name..."
            theme="employee"
            className="flex-1 max-w-xl"
          />
          {(mode === 'my-requests' || mode === 'all-requests') && (
            <FilterButton
              theme="employee"
              isOpen={showFilters}
              activeCount={statusFilter ? 1 : 0}
              onClick={() => setShowFilters(v => !v)}
            />
          )}
        </div>

        {/* Collapsible filter panel */}
        {showFilters && (mode === 'my-requests' || mode === 'all-requests') && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Status</label>
                <PillSelect
                  options={STATUS_OPTIONS}
                  value={statusFilter}
                  onChange={setStatusFilter}
                  theme="employee"
                  icon={Activity}
                  iconColor="text-blue-600"
                  className="w-full"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      {filtered.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border p-12 text-center">
          <Moon className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">
            {requests?.length === 0 ? 'No nightwork requests yet.' : 'No requests match your filters.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3">
          {filtered.map(req => (
            <NightworkRequestCard key={req.id} request={req} onView={onViewDetail} mode={mode} />
          ))}
        </div>
      )}
    </div>
  );
};

const NightworkRequestCard = ({ request, onView, mode }) => {
  const dateLabel = buildDateLabel(request);

  const hrAction = request.last_hr_action;
  const reasonText = hrAction?.reason || request.cancellation_reason;

  return (
    <div className="bg-white border rounded-xl p-4 hover:shadow-md transition-shadow flex flex-col h-full">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Moon className="w-5 h-5 text-blue-800" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-gray-900 truncate">
              {request.request_number || (
                <span className="italic text-gray-500">Draft (not yet submitted)</span>
              )}
            </h4>
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {request.project_code} &middot; {request.project_name}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <StatusBadge status={request.status} />
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
        <div className="flex items-start gap-1.5 text-xs text-gray-600 col-span-2 sm:col-span-1">
          <Calendar className="w-3 h-3 flex-shrink-0 mt-0.5" />
          <span className="line-clamp-2">{dateLabel}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <Clock className="w-3 h-3 flex-shrink-0" />
          <span>{formatTime(request.start_time)} - {formatTime(request.end_time)}</span>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <Users className="w-3 h-3 flex-shrink-0" />
          <span>{uniquePersonnelListCount({
            personnelCount: request.personnel_count,
            workerCount: request.worker_count,
            safetyCount: request.safety_count,
          })} personnel</span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 mb-3">
        <InfoBadge
          color={request.nightwork_mode === 'extension' ? 'violet' : 'slate'}
          label={request.nightwork_mode === 'extension' ? 'Extension' : 'Standard'}
          size="xs"
        />
        <InfoBadge
          color={request.recovery_type === 'am_only' ? 'amber' : 'teal'}
          label={request.recovery_type === 'am_only' ? 'AM Recovery' : 'Full Day Recovery'}
          size="xs"
        />
        {request.amendment_version > 1 && (
          <InfoBadge color="purple" label={`v${request.amendment_version} amendment`} size="xs" />
        )}
      </div>

      {hrAction && (
        <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-3.5 h-3.5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-amber-800">
                {formatHrActionBannerTitle(hrAction.type)}
              </p>
              {reasonText && (
                <p className="text-xs text-amber-700 mt-0.5 line-clamp-2">{reasonText}</p>
              )}
              <p className="text-xs text-amber-600/70 mt-1">
                {hrAction.by} &middot; {formatDate(hrAction.at)}
              </p>
            </div>
          </div>
        </div>
      )}

      {!hrAction && request.cancellation_reason && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-xs font-medium text-red-800 mb-0.5">Cancellation Reason</p>
          <p className="text-xs text-red-700 line-clamp-2">{request.cancellation_reason}</p>
        </div>
      )}

      <div className="card-action-footer justify-between flex-nowrap">
        {(mode === 'pending-approval' || mode === 'all-requests') && request.requested_by_name ? (
          <div className="flex items-center gap-1 text-xs text-gray-500 min-w-0 flex-1 mr-2">
            <User className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{request.requested_by_name}</span>
          </div>
        ) : (
          <div />
        )}
        <button
          onClick={() => onView(request.id)}
          className="p-2 text-blue-800 hover:text-blue-900 hover:bg-blue-50 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 flex-shrink-0"
          title={mode === 'pending-approval' ? 'Review request' : 'View details'}
          aria-label={mode === 'pending-approval' ? 'Review request' : 'View details'}
        >
          <Eye className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default NightworkRequestList;
